import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";

const RELATION_OPTIONS = [
    "Self",
    "Spouse",
    "Parent",
    "Child",
    "Dependent",
    "Corporate",
    "Other",
];

const ID_PROOF_OPTIONS = [
    "Aadhaar Card",
    "Passport",
    "Driver License",
    "PAN Card",
    "Voter ID",
    "Government ID",
    "Other",
];

const GENDER_OPTIONS = [
    "male",
    "female",
    "other",
];

const AGE_OPTIONS = Array.from({ length: 83 }, (_, index) => 18 + index);

const STATUS_OPTIONS = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
    { label: "Deleted", value: "deleted" },
];

const NewCustomer = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [companyId, setCompanyId] = useState(null);
    const [agentId, setAgentId] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        date_of_birth: "",
        gender: "",
        id_proof_type: "",
        id_proof_number: "",
        age: "",
        relationship: "Self",
        status: "active",
        notes: "",
    });

    useEffect(() => {
        resolveCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id]);

    const resolveCompany = async () => {
        if (!userData?.id) {
            setCompanyId(null);
            return;
        }

        setLoading(true);
        try {
            let derivedCompanyId = userData?.company_id || userData?.companyId || null;
            let derivedAgentId = userData?.agent_id || userData?.agentId || null;

            if (!derivedCompanyId) {
                const response = await oneKlicks.read("", "insurance_companies", {}, "created_at DESC", "", "", "0,1");
                if (response?.success && Array.isArray(response?.data) && response.data.length > 0) {
                    derivedCompanyId = response.data[0].id;
                }
            }

            if (derivedCompanyId && !derivedAgentId) {
                const agentResponse = await oneKlicks.read(
                    "",
                    "insurance_agents",
                    { company_id: derivedCompanyId },
                    "created_at DESC",
                    "",
                    "",
                    "0,1"
                );
                if (agentResponse?.success && Array.isArray(agentResponse?.data) && agentResponse.data.length > 0) {
                    derivedAgentId = agentResponse.data[0].id;
                }
            }

            if (!derivedCompanyId) {
                toast.error("Company information not found. Please create a company first.");
            }
            setCompanyId(derivedCompanyId || null);
            setAgentId(derivedAgentId || null);
        } catch (error) {
            console.error("Error resolving company for customer:", error);
            toast.error("Unable to determine company. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.name.trim()) {
            toast.error("Customer name is required");
            return;
        }

        if (!formData.email.trim()) {
            toast.error("Customer email is required");
            return;
        }

        if (!formData.phone.trim()) {
            toast.error("Customer phone is required");
            return;
        }

        if (!formData.address.trim()) {
            toast.error("Address is required");
            return;
        }

        if (!formData.date_of_birth) {
            toast.error("Date of birth is required");
            return;
        }

        if (!formData.id_proof_type.trim()) {
            toast.error("ID proof type is required");
            return;
        }

        if (!formData.id_proof_number.trim()) {
            toast.error("ID proof number is required");
            return;
        }

        if (!userData?.id) {
            toast.error("User information missing. Please login again.");
            return;
        }

        if (!companyId) {
            toast.error("Company information missing. Please create a company first.");
            return;
        }

        if (!agentId) {
            toast.error("Agent information missing. Please create an agent first.");
            return;
        }

        setSubmitting(true);
        try {
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            const payload = {
                name: formData.name.trim(),
                full_name: formData.name.trim(),
                customer_name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                address: formData.address.trim(),
                city: formData.city?.trim() || null,
                state: formData.state?.trim() || null,
                country: formData.country?.trim() || null,
                postal_code: formData.postal_code?.trim() || null,
                date_of_birth: formData.date_of_birth,
                gender: formData.gender || null,
                id_proof_type: formData.id_proof_type.trim(),
                id_proof_number: formData.id_proof_number.trim(),
                relationship: formData.relationship?.trim() || null,
                status: formData.status || "active",
                user_id: String(userData.id),
                company_id: companyId,
                agent_id: agentId,
                meta: JSON.stringify({
                    age: formData.age ? Number(formData.age) : null,
                    notes: formData.notes?.trim() || null,
                }),
                created_at: timestamp,
                updated_at: timestamp,
            };

            console.log("Creating insurance customer payload:", payload);
            const response = await oneKlicks.create("", "insurance_customers", payload);
            console.log("Create insurance customer response:", response);

            if (response?.success) {
                toast.success("Customer added successfully");
                navigate("/insurance/customers");
            } else {
                toast.error(response?.message || response?.error || "Failed to add customer");
            }
        } catch (error) {
            console.error("Error creating customer:", error);
            toast.error("Failed to add customer");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "24px" }}>
            <div className="dc-haslayout dc-dbsectionspace" style={{ maxWidth: "840px", margin: "0 auto" }}>
                <div className="dc-dashboardbox">
                    <div
                        className="dc-dashboardboxtitle"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <h2>Add Customer</h2>
                            <p style={{ color: "#6c757d", marginTop: "4px" }}>
                                Register a new customer so policies can be issued and linked accurately.
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="insurance-customer-form"
                                className="btn btn-primary"
                                disabled={submitting || loading}
                            >
                                {submitting ? "Saving..." : "Save Customer"}
                            </button>
                        </div>
                    </div>

                    <div className="dc-dashboardboxcontent" style={{ padding: "24px" }}>
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <form id="insurance-customer-form" className="dc-formtheme" onSubmit={handleSubmit}>
                                <div className="row" style={{ rowGap: "24px" }}>
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="name">Customer Name</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                className="form-control"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Full legal name"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="email">Email</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                className="form-control"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="name@example.com"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="phone">Phone (optional)</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                className="form-control"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+91-98765-00000"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="relationship">Relationship</label>
                                            <input
                                                list="insurance-customer-relations"
                                                id="relationship"
                                                name="relationship"
                                                className="form-control"
                                                value={formData.relationship}
                                                onChange={handleChange}
                                                placeholder="e.g. Spouse"
                                            />
                                            <datalist id="insurance-customer-relations">
                                                {RELATION_OPTIONS.map((option) => (
                                                    <option key={option} value={option} />
                                                ))}
                                            </datalist>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="status">Status</label>
                                            <select
                                                id="status"
                                                name="status"
                                                className="form-control"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                {STATUS_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="date_of_birth">Date of Birth</label>
                                            <input
                                                type="date"
                                                id="date_of_birth"
                                                name="date_of_birth"
                                                className="form-control"
                                                value={formData.date_of_birth}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <div className="form-group">
                                            <label htmlFor="age">Age</label>
                                            <select
                                                id="age"
                                                name="age"
                                                className="form-control"
                                                value={formData.age}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Age</option>
                                                {AGE_OPTIONS.map((age) => (
                                                    <option key={age} value={age}>
                                                        {age}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <div className="form-group">
                                            <label htmlFor="gender">Gender</label>
                                            <select
                                                id="gender"
                                                name="gender"
                                                className="form-control"
                                                value={formData.gender}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Gender</option>
                                                {GENDER_OPTIONS.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option.charAt(0).toUpperCase() + option.slice(1)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="id_proof_type">ID Proof Type</label>
                                            <input
                                                list="insurance-customer-idproof"
                                                id="id_proof_type"
                                                name="id_proof_type"
                                                className="form-control"
                                                value={formData.id_proof_type}
                                                onChange={handleChange}
                                                placeholder="e.g. Aadhaar Card"
                                                required
                                            />
                                            <datalist id="insurance-customer-idproof">
                                                {ID_PROOF_OPTIONS.map((option) => (
                                                    <option key={option} value={option} />
                                                ))}
                                            </datalist>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="id_proof_number">ID Proof Number</label>
                                            <input
                                                type="text"
                                                id="id_proof_number"
                                                name="id_proof_number"
                                                className="form-control"
                                                value={formData.id_proof_number}
                                                onChange={handleChange}
                                                placeholder="Enter the ID number"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="mb-3" style={{ marginTop: "8px" }}>
                                            <h5 style={{ marginBottom: "4px" }}>Address</h5>
                                            <p style={{ color: "#6c757d", marginBottom: 0 }}>
                                                Mailing address for communications and policy dispatch.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="address">Street Address</label>
                                            <textarea
                                                id="address"
                                                name="address"
                                                className="form-control"
                                                rows="2"
                                                value={formData.address}
                                                onChange={handleChange}
                                                placeholder="House/Flat, Street, Landmark"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-3">
                                        <div className="form-group">
                                            <label htmlFor="city">City</label>
                                            <input
                                                type="text"
                                                id="city"
                                                name="city"
                                                className="form-control"
                                                value={formData.city}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <div className="form-group">
                                            <label htmlFor="state">State</label>
                                            <input
                                                type="text"
                                                id="state"
                                                name="state"
                                                className="form-control"
                                                value={formData.state}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <div className="form-group">
                                            <label htmlFor="country">Country</label>
                                            <input
                                                type="text"
                                                id="country"
                                                name="country"
                                                className="form-control"
                                                value={formData.country}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <div className="form-group">
                                            <label htmlFor="postal_code">Postal Code</label>
                                            <input
                                                type="text"
                                                id="postal_code"
                                                name="postal_code"
                                                className="form-control"
                                                value={formData.postal_code}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="notes">Notes (optional)</label>
                                            <textarea
                                                id="notes"
                                                name="notes"
                                                className="form-control"
                                                rows="3"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                placeholder="Policy references, contact context, or other details"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewCustomer;
